import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const SYMBOLIC = '#DCB13C';
const TEXT_MUTED = '#5a5f7d';

export function DetailLossFunnel() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const centerX = width / 2;

    // Phase 1: Rich description fragments
    const richPhrases = [
      { text: 'A person working', x: centerX - 70, y: 28 },
      { text: '8 hours', x: centerX + 50, y: 28 },
      { text: 'feeling tired', x: centerX - 55, y: 48 },
      { text: 'caring for family', x: centerX + 40, y: 48 },
      { text: 'hoping for rest', x: centerX - 30, y: 68 },
      { text: 'years of experience', x: centerX + 30, y: 68 },
    ];

    // Phase 2: Variables
    const variables = [
      { text: 'hours = 8', x: centerX - 40, y: 48 },
      { text: 'output = x', x: centerX + 40, y: 48 },
    ];

    // Phase 3: Equation
    const equation = { text: 'y = 8x', x: centerX, y: 48 };

    // Draw funnel shape (background guide)
    const funnelGroup = svg.append('g').attr('opacity', 0.12);

    funnelGroup
      .append('path')
      .attr('d', `
        M ${centerX - 130} 12
        L ${centerX + 130} 12
        L ${centerX + 30} 90
        L ${centerX - 30} 90
        Z
      `)
      .attr('fill', SYMBOLIC)
      .attr('stroke', 'none');

    // Phase 1: Show rich text
    const richGroup = svg.append('g').attr('class', 'rich');

    richPhrases.forEach((phrase, i) => {
      richGroup
        .append('text')
        .attr('x', phrase.x)
        .attr('y', phrase.y)
        .attr('text-anchor', 'middle')
        .attr('font-size', 8.5)
        .attr('class', 'viz-text')
        .attr('opacity', 0)
        .text(phrase.text)
        .transition()
        .duration(300)
        .delay(i * 100)
        .attr('opacity', 0.85);
    });

    // Horizontal divider
    const divider1Y = 100;
    svg.append('line')
      .attr('x1', 40).attr('y1', divider1Y)
      .attr('x2', 40).attr('y2', divider1Y)
      .attr('stroke', TEXT_MUTED).attr('stroke-width', 0.8)
      .attr('stroke-dasharray', '3,3').attr('opacity', 0.4)
      .transition().duration(400).delay(800)
      .attr('x2', width - 40);

    // Phase 2: Variables appear below
    const varGroup = svg.append('g').attr('class', 'variables');
    const varY = 125;

    variables.forEach((v, i) => {
      varGroup
        .append('text')
        .attr('x', v.x)
        .attr('y', varY)
        .attr('text-anchor', 'middle')
        .attr('font-size', 11)
        .attr('font-weight', 600)
        .attr('font-family', 'monospace')
        .attr('fill', SYMBOLIC)
        .attr('opacity', 0)
        .text(v.text)
        .transition()
        .duration(400)
        .delay(1200 + i * 200)
        .attr('opacity', 0.9);
    });

    // Fade rich text after variables appear
    const fadeRichTimer = d3.timeout(() => {
      richGroup
        .selectAll('text')
        .transition()
        .duration(500)
        .attr('opacity', 0.2);

      // Strikethrough effect
      richPhrases.forEach((phrase, i) => {
        if (i > 1) {
          richGroup
            .append('line')
            .attr('x1', phrase.x - 30)
            .attr('y1', phrase.y - 3)
            .attr('x2', phrase.x - 30)
            .attr('y2', phrase.y - 3)
            .attr('stroke', TEXT_MUTED)
            .attr('stroke-width', 1)
            .attr('opacity', 0.5)
            .transition()
            .duration(300)
            .delay(i * 80)
            .attr('x2', phrase.x + 30);
        }
      });
    }, 1800);

    // Divider 2
    const divider2Y = 142;
    const div2Timer = d3.timeout(() => {
      svg.append('line')
        .attr('x1', 60).attr('y1', divider2Y)
        .attr('x2', 60).attr('y2', divider2Y)
        .attr('stroke', TEXT_MUTED).attr('stroke-width', 0.8)
        .attr('stroke-dasharray', '3,3').attr('opacity', 0.4)
        .transition().duration(400)
        .attr('x2', width - 60);
    }, 2400);

    // Phase 3: Clean equation
    const eqTimer = d3.timeout(() => {
      // Fade variables
      varGroup
        .selectAll('text')
        .transition()
        .duration(400)
        .attr('opacity', 0.25);

      // Show equation
      svg.append('text')
        .attr('x', equation.x)
        .attr('y', 168)
        .attr('text-anchor', 'middle')
        .attr('font-size', 18)
        .attr('font-weight', 700)
        .attr('font-family', 'monospace')
        .attr('fill', SYMBOLIC)
        .attr('opacity', 0)
        .text(equation.text)
        .transition()
        .duration(500)
        .attr('opacity', 1);

      // Arrow pointing down
      svg.append('text')
        .attr('x', centerX)
        .attr('y', 108)
        .attr('text-anchor', 'middle')
        .attr('font-size', 12)
        .attr('fill', TEXT_MUTED)
        .attr('opacity', 0)
        .text('\u2193')
        .transition()
        .duration(300)
        .delay(200)
        .attr('opacity', 0.5);

      svg.append('text')
        .attr('x', centerX)
        .attr('y', 148)
        .attr('text-anchor', 'middle')
        .attr('font-size', 12)
        .attr('fill', TEXT_MUTED)
        .attr('opacity', 0)
        .text('\u2193')
        .transition()
        .duration(300)
        .delay(300)
        .attr('opacity', 0.5);
    }, 3000);

    return () => {
      fadeRichTimer.stop();
      div2Timer.stop();
      eqTimer.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-square-root-variable" /> Math &mdash; Precision Tradeoff
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 180" />
      <p className="viz-container__caption">
        Precision achieved by losing detail.
      </p>
    </div>
  );
}
